import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Network } from 'lucide-react';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Spinner,
} from '@/components/ui';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useCreateHive } from '@/features/agents/hives/hooks/useHives';
import type { HiveCreate } from '@/features/agents/hives/types';

// =============================================================================
// Hive Create Page
// =============================================================================

export function HiveCreatePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const createMutation = useCreateHive();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [routerEnabled, setRouterEnabled] = useState(false);
  const [routerPrompt, setRouterPrompt] = useState('');

  const canSubmit = name.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      const data: HiveCreate = {
        name: name.trim(),
        description: description.trim() || undefined,
        router_enabled: routerEnabled,
        router_prompt: routerPrompt.trim() || undefined,
      };
      const hive = await createMutation.mutateAsync(data);
      toast.success(t('admin.hives.createSuccess') || 'Hive created');
      navigate(`/agents/hives/${hive.id}`);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || t('admin.hives.createError') || 'Failed to create hive');
    }
  };

  return (
    <div className="p-6" data-testid="hive-create-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/agents/hives')}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
            <Network className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('admin.hives.newHive')}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              {t('admin.hives.createSubtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card className="p-6 space-y-6 max-w-2xl">
          <div>
            <Label htmlFor="hive-name">{t('admin.hives.form.name')}</Label>
            <Input
              id="hive-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('admin.hives.form.namePlaceholder')}
              className="mt-1"
              required
            />
          </div>

          <div>
            <Label htmlFor="hive-description">{t('admin.hives.form.description')}</Label>
            <Textarea
              id="hive-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('admin.hives.form.descriptionPlaceholder')}
              className="mt-1"
              rows={3}
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="router-enabled"
              checked={routerEnabled}
              onChange={(e) => setRouterEnabled(e.target.checked)}
              className="h-4 w-4 text-purple-600 rounded"
            />
            <Label htmlFor="router-enabled">{t('admin.hives.form.routerEnabled')}</Label>
          </div>

          {routerEnabled && (
            <div>
              <Label htmlFor="router-prompt">{t('admin.hives.form.routerPrompt')}</Label>
              <Textarea
                id="router-prompt"
                value={routerPrompt}
                onChange={(e) => setRouterPrompt(e.target.value)}
                className="mt-1"
                rows={5}
                placeholder={t('admin.hives.form.routerPromptPlaceholder')}
              />
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" type="button" onClick={() => navigate('/agents/hives')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit || createMutation.isPending}>
              {createMutation.isPending ? <Spinner size="sm" /> : t('common.create')}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
